import type { ActingFor, WriteSetting } from '@myrewards/contracts';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useCallback, type ReactElement } from 'react';
import { meQuery } from '../api';
import { ActForConfirm } from '../acting-for/ActForConfirm';
import { useExecutivesToName } from '../acting-for/useActForPeople';
import { firstName, recordedLine, saySoFromTheNotice, withNoteAndFiles } from '../acting-for/words';
import { useToast, type ConfirmDialogProps } from '../ui';
import { useWriteSetting } from './parts';

/**
 * The same save as `useSettingWrite`'s, for a change marked "For an Executive" (APP-148 D20): a
 * refusal is thrown, so the step keeps it in its own words; the toast says whom it was done for.
 */
export function useSaveFor(): (
  body: WriteSetting & { actingFor: ActingFor },
  saved: string,
  forName: string,
) => Promise<void> {
  const write = useWriteSetting();
  const toast = useToast();
  return useCallback(
    async (body: WriteSetting & { actingFor: ActingFor }, saved: string, forName: string) => {
      await write(body);
      toast.show({
        tone: 'done',
        title: saved,
        body: `It’s in the history with your name, done for ${forName}, who is told.`,
      });
    },
    [write, toast],
  );
}

/** "Default TAA split: 50% PAA/TAA pool · 50% sector pool → 60% …": a confirm row as a line. */
function rowLine(row: ConfirmDialogProps['summary'][number]): string {
  return `${row.label}: ${row.before === undefined ? '' : `${row.before} → `}${row.value}`;
}

/**
 * The confirm for a setting an Executive can change (APP-148 D20, Setting board): for a Stock Admin
 * while acting for Executives is on, "Who is this change for? Just me / For an Executive". The
 * second shows the step: the Executive, the reason, files and a note, what changes, and how it's
 * recorded. A marker, never new authority: the server still checks the Stock Admin's own right.
 */
export function SettingConfirm({
  subject,
  onConfirmFor,
  ...dialog
}: ConfirmDialogProps & {
  /** The setting's name, over the step: "Default TAA funding split". */
  subject: string;
  /** Saves the change for the Executive named, with the reason; throws to keep the step open. */
  onConfirmFor: (reason: string, actingFor: ActingFor, forName: string) => Promise<void>;
}): ReactElement {
  const { data: me } = useSuspenseQuery(meQuery);
  const offer = useExecutivesToName();
  const actorName = me.person.displayName;
  const nameOf = (personId: string | null): string | null => {
    const person = offer?.people.find((p) => p.id === personId);
    return person === undefined ? null : firstName(person.name);
  };
  return (
    <ActForConfirm
      {...dialog}
      actFor={
        offer === null
          ? null
          : {
              question: 'Who is this change for?',
              someone: 'For an Executive',
              step: {
                title: 'Change for an Executive',
                subtitle: subject,
                intro:
                  'You’re making this change for an Executive. It’s recorded as your change, done for them, and they’re told.',
                actorName,
                offer,
                firstNameOf: (id) => nameOf(id) ?? '',
                whoLabel: 'Executive',
                whoHint:
                  'Name the Executive who asked. They’re told, and the history says it was done for them.',
                noteHint: (name) => `${name} sees it in the notice. It’s kept with the change.`,
                summaryTitle: 'Check before saving',
                summary: (personId, added) => (
                  <>
                    {dialog.summary.map((row) => (
                      <li key={row.label}>{rowLine(row)}</li>
                    ))}
                    {/* The dialog's intro gives way to the step; what it says stays, as a line. */}
                    {dialog.intro !== undefined && <li>{dialog.intro}</li>}
                    <li>
                      {`${nameOf(personId) ?? 'The Executive you name'} gets a notice in the bell and by email${withNoteAndFiles(added.note, added.files)}.`}
                    </li>
                  </>
                ),
                recorded: (person) => recordedLine('Changed', actorName, person, 'executive'),
                confirmLabel: (person) => `${dialog.confirmLabel} for ${firstName(person.name)}`,
                unnamedLabel: dialog.confirmLabel,
                ifNotWanted: saySoFromTheNotice,
              },
              onConfirm: (reason, actingFor, done) =>
                onConfirmFor(reason, actingFor, done.person.name),
            }
      }
    />
  );
}
